// A world atlas (issue 1173): scene links on the board, and the DM's way back along them.
// `LinkMarkers` draws a marker for each link this person holds — the DM's every one (an unshown one dashed), a player's
// only the shown ones the server sent, which carry a label and nothing of where they lead. The DM's click offers "Go
// there" (the table's live scene becomes the linked one: the ordinary go-live, then this window follows) and "Open"
// (the DM alone stages it). A player's click shows the label and does nothing else: the DM moves the table.
// `AtlasCrumbs` is the DM's trail (`atlas.ts`, client state): world › region › town, each step a way back.
import { useEffect, useState } from 'react';
import type { PlayerLink, SceneLink } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { useShell } from '../shell/context';
import { goLive } from '../table';
import { atlasTrail, useAtlasTrail } from '../atlas';
import { boardBus, type BoardViewState } from './bus';
import { BTN } from '../shell/ui';

const btn = BTN;

function useView(): BoardViewState | null {
  const [v, setV] = useState<BoardViewState | null>(null);
  useEffect(() => boardBus.onView(setV), []);
  return v;
}

const isDmLink = (l: SceneLink | PlayerLink): l is SceneLink => 'toScene' in l;

/** Follow a link (the DM's): "Go there" moves the table first; either way this window opens the linked scene and the trail grows. */
export function useFollowLink() {
  const { table, campaignId, onResult } = useShell();
  const { scene, viewScene, setNotice } = useTableState();
  return async (to: string, moveTable: boolean): Promise<void> => {
    const here = (table.scenes ?? []).find((s) => s.id === scene?.id);
    if (moveTable) {
      const r = await goLive(campaignId, to);
      onResult(r);
      if (r.kind !== 'ok') { setNotice((r as { error?: string }).error ?? 'The table could not be moved there.'); return; }
    }
    if (here) atlasTrail.follow({ sceneId: here.id, name: here.name }, moveTable);
    viewScene(to);
  };
}

export function LinkMarkers() {
  const { store, role, act, viewingAs } = useTableState();
  const mayAct = useMayAct();
  const { table } = useShell();
  const view = useView();
  const follow = useFollowLink();
  const [open, setOpen] = useState<string | null>(null);
  const links = Object.values((store.state.links ?? {}) as Record<string, SceneLink | PlayerLink>);
  const opened = open ? links.find((l) => l.id === open) : undefined;
  useEffect(() => { if (open && !opened) setOpen(null); }, [open, opened]);
  if (!view || !(view.view.zoom > 0) || !links.length) return null;
  const dm = role === 'dm' && !viewingAs && mayAct('link.set');
  const sceneName = (id: string) => (table.scenes ?? []).find((s) => s.id === id)?.name ?? null;
  return (
    <>
      {links.map((l) => {
        const sx = (l.at.x - view.view.x) * view.view.zoom; const sy = (l.at.y - view.view.y) * view.view.zoom;
        if (sx < -40 || sy < -20 || sx > view.viewport.width + 40 || sy > view.viewport.height + 20) return null;
        const hidden = isDmLink(l) && !l.shown;
        return (
          <button key={l.id} type="button" aria-label={`Scene link: ${l.label}`} data-testid="scene-link" data-shown={isDmLink(l) ? String(l.shown) : 'true'}
            title={hidden ? `${l.label} (hidden from players)` : l.label}
            className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 rounded-full border-2 px-2 py-0.5 text-xs font-semibold shadow ${hidden ? 'border-dashed border-sky-200 bg-sky-950/80 text-sky-100' : 'border-white bg-sky-700 text-white'}`}
            style={{ left: sx, top: sy }} onClick={() => setOpen(l.id)}>
            <span aria-hidden="true">⇲</span>{l.label}
          </button>
        );
      })}
      {opened && (
        <div role="dialog" aria-label={`Scene link ${opened.label}`} data-testid="scene-link-dialog" className="pointer-events-auto absolute right-2 top-12 w-72 vtt-popover p-3 text-sm text-stone-200 shadow-lg">
          <h3 className="vtt-title">{opened.label}</h3>
          {dm && isDmLink(opened) ? (
            <>
              <p className="text-xs text-stone-400">Leads to {sceneName(opened.toScene) ? <strong>{sceneName(opened.toScene)}</strong> : 'a scene that is gone'}. {opened.shown ? 'Players see this marker.' : 'Hidden from players.'}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                <button type="button" className={btn} disabled={!sceneName(opened.toScene)} onClick={() => { setOpen(null); void follow(opened.toScene, true); }}>Go there</button>
                <button type="button" className={btn} disabled={!sceneName(opened.toScene)} title="Open it for yourself; the players stay where they are." onClick={() => { setOpen(null); void follow(opened.toScene, false); }}>Open</button>
                <button type="button" className={btn} aria-pressed={opened.shown} onClick={() => act({ kind: 'link.set', link: { ...opened, shown: !opened.shown } })}>{opened.shown ? 'Hide from players' : 'Show to players'}</button>
                <button type="button" className={btn} onClick={() => act({ kind: 'link.remove', id: opened.id })}>Remove</button>
              </div>
            </>
          ) : <p className="text-xs text-stone-400">A place on this map. The DM moves the table.</p>}
          <button type="button" className={`${btn} mt-2`} onClick={() => setOpen(null)}>Close</button>
        </div>
      )}
    </>
  );
}

/** The DM's way back (issue 1173): the scenes they came by through links, oldest first, then where they are. */
export function AtlasCrumbs() {
  const { table, campaignId, onResult } = useShell();
  const { role, scene, viewScene, viewingAs } = useTableState();
  const trail = useAtlasTrail();
  const scenes = table.scenes;
  useEffect(() => { if (scenes) atlasTrail.prune(new Set(scenes.map((s) => s.id))); }, [scenes]);
  if (role !== 'dm' || viewingAs || !trail.length || !scene) return null;
  const here = (scenes ?? []).find((s) => s.id === scene.id);
  const back = async (i: number) => {
    const step = trail[i]!;
    if (atlasTrail.backTo(i)) onResult(await goLive(campaignId, step.sceneId));
    viewScene(step.sceneId);
  };
  return (
    <nav aria-label="Back along scene links" data-testid="atlas-crumbs" className="pointer-events-auto flex items-center gap-1 rounded bg-stone-900/90 px-2 py-0.5 text-xs text-stone-200">
      <span className="text-stone-400">Back:</span>
      {trail.map((s, i) => (
        <span key={`${s.sceneId}-${i}`} className="flex items-center gap-1">
          <button type="button" className="underline hover:text-white" title={s.moved ? 'Takes the table back there too.' : 'Opens it for you alone.'} onClick={() => void back(i)}>{s.name}</button>
          <span aria-hidden="true">›</span>
        </span>
      ))}
      <span aria-current="location" className="font-semibold">{here?.name ?? 'here'}</span>
    </nav>
  );
}
